import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, isValid, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import NewReservationModal from '@/components/reservations/NewReservationModal';
import AvailabilitySummaryBar from '../components/reservations/AvailabilitySummaryBar';
import StaffColumnCalendarView from '@/components/reservations/StaffColumnCalendarView';
import AppointmentListView from '@/components/reservations/AppointmentListView';
import ExtendedAppointmentDetails from '@/components/reservations/ExtendedAppointmentDetails';
// Supabase統合: PR244 UI維持 + 最小限データ統合
import { DataService } from '@/services/dataService';
import { useBusinessHoursCalendar } from '../hooks/useBusinessHoursCalendar';
import type { ExtendedReservation } from '@/types/salonboard';
import { useStore } from '@/store/useStore';
import { serviceManagementService } from '@/services/serviceManagementService';
import { usePageTitle } from '@/hooks/usePageTitle';
import logger from '../utils/logger';

// PR244互換Reservation型定義（UI表示用）
interface Reservation {
  id: string;
  customerId: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  staffId: string;
  staffName: string;
  serviceId: string;
  serviceName: string;
  date: Date;
  startTime: string;
  endTime: string;
  duration: number;
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed';
  notes?: string;
  color: string;
  changeHistory?: any[];
  isVip?: boolean;
  price?: number;
  customerTier?: 'regular' | 'gold' | 'platinum' | 'diamond';
}

// ExtendedReservation → PR244 Reservation 変換関数（Supabase統合用）
const convertExtendedToReservation = (extended: ExtendedReservation): Reservation => {
  // 日付処理: 文字列またはDateオブジェクトを適切に変換
  let dateObj: Date;
  if (typeof extended.date === 'string') {
    dateObj = new Date(extended.date);
  } else if (extended.date instanceof Date) {
    dateObj = extended.date;
  } else {
    dateObj = new Date(); // フォールバック
  }

  return {
    id: extended.id,
    customerId: extended.customerId,
    customerName: extended.customerName,
    customerPhone: extended.customerPhone || '090-0000-0000',
    customerEmail: extended.customerEmail || 'customer@example.com',
    staffId: extended.staffId,
    staffName: extended.staffName,
    serviceId: extended.serviceId,
    serviceName: extended.serviceName,
    date: dateObj,
    startTime: extended.startTime,
    endTime: extended.endTime,
    duration: extended.duration,
    status: extended.status as 'confirmed' | 'pending' | 'cancelled',
    notes: extended.notes || '',
    color: '#6c5ce7', // デフォルト色（モック互換）
    isVip: extended.isVip || false,
    price: extended.price,
    customerTier: (extended.customerTier as 'regular' | 'gold' | 'platinum' | 'diamond') || 'regular'
  };
};

// Reservation → ExtendedReservation 変換関数（保存用）
const convertReservationToExtended = (reservation: Reservation): ExtendedReservation => {
  // 基本的なExtendedReservation型のフィールドを設定
  return {
    id: reservation.id,
    customerId: reservation.customerId,
    customerName: reservation.customerName,
    customerPhone: reservation.customerPhone,
    customerEmail: reservation.customerEmail,
    staffId: reservation.staffId,
    staffName: reservation.staffName,
    serviceId: reservation.serviceId,
    serviceName: reservation.serviceName,
    date: reservation.date,
    startTime: reservation.startTime,
    endTime: reservation.endTime,
    duration: reservation.duration,
    status: reservation.status,
    notes: reservation.notes || '',
    isVip: reservation.isVip || false,
    price: reservation.price,
    customerTier: reservation.customerTier || 'regular',
    // ExtendedReservation特有のフィールド（デフォルト値設定）
    reservationNumber: `RES-${Date.now()}-${reservation.id}`,
    source: 'WALK_IN' as any,
    detailedStatus: 'DRAFT' as any,
    amount: {
      subtotal: reservation.price || 0,
      tax: 0,
      total: reservation.price || 0,
      discount: 0
    },
    visitProcessing: {
      status: 'PENDING' as any,
      deadline: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000) // 6日後
    },
    points: {
      earned: 0,
      used: 0
    },
    cancellationPolicy: {
      type: 'SAME_DAY_50' as any,
      rate: 50
    },
    createdAt: new Date(),
    updatedAt: new Date()
  };
};

// 安全な日付フォーマット関数（PR244互換 + Supabase統合）
const safeDateFormat = (date: any, formatString: string): string => {
  try {
    // Dateオブジェクトの場合
    if (date instanceof Date) {
      return isValid(date) ? format(date, formatString) : '';
    }
    // 文字列の場合
    if (typeof date === 'string') {
      const parsedDate = parseISO(date);
      return isValid(parsedDate) ? format(parsedDate, formatString) : '';
    }
    // その他の場合は空文字を返す
    return '';
  } catch (error) {
    logger.warn('Date format error:', error, 'Date value:', date);
    return '';
  }
};

// スタッフのモックデータ
const mockStaff = [
  { id: 'staff1', name: '佐藤', color: '#6c5ce7' },
  { id: 'staff2', name: '田中', color: '#fd79a8' },
  { id: 'staff3', name: '山田', color: '#00b894' },
  { id: 'staff4', name: '鈴木', color: '#fdcb6e' },
];

// Storeからサービスデータを使用
// const mockServicesは削除し、Reservationsコンポーネント内で動的に生成


const Reservations: React.FC = () => {
  usePageTitle('予約管理');
  const storeServices = useStore(state => state.services);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newReservationModalOpen, setNewReservationModalOpen] = useState(false);
  // スタッフ選択は削除（スタッフ別列表示に変更）
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [selectedAppointment, setSelectedAppointment] = useState<Reservation | null>(null);
  const [customers, setCustomers] = useState<any[]>([]);

  // 顧客データ取得（DataService経由）
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const customersData = await DataService.getCustomers();
        setCustomers(customersData);
        logger.log('✅ Customers loaded:', customersData.length);
      } catch (err) {
        logger.error('❌ Failed to load customers:', err);
      }
    };
    loadCustomers();
  }, []);

  // Storeのサービスを予約画面用に変換（アクティブなもののみ）
  const services = React.useMemo(() => {
    logger.log('[Reservations] Store services updated:', storeServices);

    if (storeServices && storeServices.length > 0) {
      // isActiveがfalseでないサービスのみフィルタリング（undefined or trueの場合は表示）
      return storeServices
        .filter(s => s.isActive !== false)
        .map(s => ({
          id: s.id,
          name: s.name,
          duration: s.duration,
          price: s.price
        }));
    }
    // フォールバック: デフォルトサービス
    return [
      { id: 'service1', name: 'ドライヘッドスパ 60分', duration: 60, price: 6000 },
      { id: 'service2', name: 'ドライヘッドスパ 90分', duration: 90, price: 8500 },
      { id: 'service3', name: 'アロマヘッドスパ 60分', duration: 60, price: 7000 },
      { id: 'service4', name: 'カット + ヘッドスパ', duration: 120, price: 9500 },
      { id: 'service5', name: 'カラー + ヘッドスパ', duration: 180, price: 14000 },
    ];
  }, [storeServices]);

  // 営業時間カレンダー連携
  const startDate = useMemo(() => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - 7);
    return start.toISOString().split('T')[0];
  }, [currentDate]);
  
  const endDate = useMemo(() => {
    const end = new Date(currentDate);
    end.setDate(end.getDate() + 14);
    return end.toISOString().split('T')[0];
  }, [currentDate]);
  
  const {
    validateReservationTime,
    isOpenAt,
    getBusinessHoursForDate,
    loading: businessHoursLoading,
    error: businessHoursError,
  } = useBusinessHoursCalendar(startDate, endDate);

  // 通知表示のヘルパー（sonner toast使用）
  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    switch (severity) {
      case 'success':
        toast.success(message);
        break;
      case 'error':
        toast.error(message);
        break;
      case 'warning':
        toast.warning(message);
        break;
      case 'info':
        toast.info(message);
        break;
    }
  };

  // 日付変更関数
  const changeDate = (days: number) => {
    setCurrentDate(prev => addDays(prev, days));
  };

  // DataService統一実装: ダッシュボードと完全同期
  useEffect(() => {
    const loadReservations = async () => {
      try {
        logger.log('🔄 Loading reservations via unified DataService...');
        // サービス管理サービスを初期化
        await serviceManagementService.initialize();
        setLoading(true);
        setError(null);
        
        // DataService経由で統一実データ取得（ダッシュボードと同一ソース）
        const realData = await DataService.getReservations();
        
        // ExtendedReservation → Reservationに変換（UI表示用）
        const convertedData = realData.map(convertExtendedToReservation);
        setReservations(convertedData);
        
        logger.log(`✅ DataService unified data loaded: ${convertedData.length} reservations`);
        logger.log('🔍 Reservations Page Data Integration Debug:', {
          source: 'DataService (unified with Dashboard)',
          totalReservations: convertedData.length,
          dataIntegrityCheck: {
            hasReservations: convertedData.length > 0,
            allReservationsHaveRequiredFields: convertedData.every(r => 
              r.id && r.customerName && r.startTime && r.date
            ),
            uniqueIds: new Set(convertedData.map(r => r.id)).size === convertedData.length
          },
          sampleReservations: convertedData.slice(0, 3).map((r, index) => ({
            index: index + 1,
            id: r.id,
            customerName: r.customerName,
            date: r.date,
            startTime: r.startTime,
            status: r.status,
            serviceName: r.serviceName
          })),
          todayReservationsCount: convertedData.filter(r => {
            const today = new Date().toISOString().split('T')[0];
            const resDate = r.date instanceof Date 
              ? r.date.toISOString().split('T')[0] 
              : String(r.date).split('T')[0];
            return resDate === today && r.status !== 'cancelled';
          }).length
        });
        
      } catch (err) {
        logger.error('❌ Failed to load reservations via DataService:', err);
        setError(err instanceof Error ? err.message : '予約データの読み込みに失敗しました');
        setReservations([]);
      } finally {
        setLoading(false);
      }
    };

    loadReservations();
  }, []);

  // 当日の予約をフィルタ（PR244互換）
  const todayAppointments = useMemo(() => {
    const selectedDateString = format(currentDate, 'yyyy-MM-dd');
    const filtered = reservations;
    
    return filtered.filter(res => {
      const resDateString = format(res.date, 'yyyy-MM-dd');
      return resDateString === selectedDateString;
    });
  }, [reservations, currentDate]);

  // 統計情報
  const stats = useMemo(() => {
    const totalRevenue = todayAppointments.reduce((sum, app) => sum + (app.price || 0), 0);
    return {
      appointmentCount: todayAppointments.length,
      totalRevenue,
    };
  }, [todayAppointments]);

  // データ変換関数
  const convertReservationsToAppointments = useCallback((reservations: Reservation[]) => {
    return reservations.map(reservation => ({
      date: safeDateFormat(reservation.date, 'yyyy-MM-dd'),
      time: reservation.startTime,
      customerId: reservation.customerId,
      customerName: reservation.customerName,
      serviceName: reservation.serviceName,
      staffName: reservation.staffName,
      duration: reservation.duration,
      status: reservation.status,
    }));
  }, []);

  const filteredReservations = useMemo(() => {
    return reservations;
  }, [reservations]);

  const appointmentsData = useMemo(() => 
    convertReservationsToAppointments(filteredReservations), 
    [filteredReservations, convertReservationsToAppointments]
  );

  const selectedDateString = useMemo(() => 
    format(currentDate, 'yyyy-MM-dd'), 
    [currentDate]
  );

  // 時間帯クリックハンドラー（営業時間考慮）
  const handlePeriodClick = useCallback((periodId: string) => {
    const selectedDateString = format(currentDate, 'yyyy-MM-dd');
    const businessHours = getBusinessHoursForDate(selectedDateString);
    
    if (!businessHours || !businessHours.isOpen) {
      showNotification('選択された日は営業日ではありません', 'warning');
      return;
    }
    
    // 営業時間に基づく適切な時間を取得
    let targetTime: string;
    
    if (businessHours.timeSlots.length > 0) {
      const firstSlot = businessHours.timeSlots[0];
      const lastSlot = businessHours.timeSlots[businessHours.timeSlots.length - 1];
      
      switch (periodId) {
        case 'morning':
          targetTime = firstSlot.start;
          break;
        case 'afternoon': {
          // 営業時間の中間点を計算
          const firstStartMinutes = timeToMinutes(firstSlot.start);
          const lastEndMinutes = timeToMinutes(lastSlot.end);
          const midMinutes = Math.floor((firstStartMinutes + lastEndMinutes) / 2);
          targetTime = `${Math.floor(midMinutes / 60).toString().padStart(2, '0')}:${(midMinutes % 60).toString().padStart(2, '0')}`;
          break;
        }
        case 'evening':
          // 夜間は17:00に固定
          targetTime = '17:00';
          break;
        default:
          targetTime = firstSlot.start;
      }
    } else {
      // フォールバック（営業時間データが不完全な場合）
      const timeMap = {
        morning: '09:00',
        afternoon: '13:00', 
        evening: '17:00',
      } as const;
      targetTime = timeMap[periodId as keyof typeof timeMap] || '09:00';
    }
    
    // 該当時間のカードを探してスクロール（レンダリング完了を待つ）
    setTimeout(() => {
      // スクロール可能なコンテナを取得
      const scrollContainer = document.querySelector('.max-h-\\[70vh\\].overflow-auto');
      const targetElement = document.querySelector(`[data-time="${targetTime}"]`);

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      } else if (scrollContainer) {
        // data-time属性を持つ要素が見つからない場合、時刻セルを探す
        const timeCell = Array.from(document.querySelectorAll('td')).find(
          (cell) => cell.textContent?.trim() === targetTime
        );
        if (timeCell) {
          timeCell.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }, 300);
  }, [currentDate, getBusinessHoursForDate]);
  
  // 時間を分に変換するヘルパー関数
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // 新規予約の保存（ExtendedReservation対応 + 営業時間検証）
  const handleSaveReservation = async (newReservation: ExtendedReservation) => {
    try {
      // 営業時間検証
      const reservationDate = newReservation.date instanceof Date 
        ? newReservation.date.toISOString().split('T')[0]
        : String(newReservation.date).split('T')[0];
      
      const validation = validateReservationTime(
        reservationDate,
        newReservation.startTime,
        newReservation.endTime
      );
      
      if (!validation.isValid) {
        const errorMessage = validation.errors.join('\n');
        showNotification(`予約時間エラー:\n${errorMessage}`, 'error');
        return;
      }
      
      // 警告がある場合は確認ダイアログ表示
      if (validation.warnings.length > 0) {
        const warningMessage = validation.warnings.join('\n');
        const confirmBooking = window.confirm(
          `以下の注意事項があります：\n${warningMessage}\n\n予約を登録しますか？`
        );
        if (!confirmBooking) {
          return;
        }
      }
      
      // ExtendedReservation → Reservation 変換（UI表示用）
      const convertedReservation = convertExtendedToReservation(newReservation);
      const updatedReservations = [...reservations, convertedReservation];
      
      // ExtendedReservation配列として保存（Supabase統合）
      const extendedReservations = [...reservations.map(convertReservationToExtended), newReservation];
      await DataService.saveReservations(extendedReservations);
      
      // UI状態更新
      setReservations(updatedReservations);
      setNewReservationModalOpen(false);
      showNotification('予約を登録しました', 'success');
    } catch (error) {
      logger.error('予約保存エラー:', error);
      showNotification('予約の登録に失敗しました', 'error');
    }
  };

  // 予約の更新（営業時間検証付き）
  const handleReservationUpdate = async (updatedReservation: Reservation) => {
    try {
      // 営業時間検証
      const reservationDate = updatedReservation.date instanceof Date 
        ? updatedReservation.date.toISOString().split('T')[0]
        : String(updatedReservation.date).split('T')[0];
      
      const validation = validateReservationTime(
        reservationDate,
        updatedReservation.startTime,
        updatedReservation.endTime
      );
      
      if (!validation.isValid) {
        const errorMessage = validation.errors.join('\n');
        showNotification(`予約時間エラー:\n${errorMessage}`, 'error');
        return;
      }
      
      // 警告がある場合は確認ダイアログ表示
      if (validation.warnings.length > 0) {
        const warningMessage = validation.warnings.join('\n');
        const confirmUpdate = window.confirm(
          `以下の注意事項があります：\n${warningMessage}\n\n予約を更新しますか？`
        );
        if (!confirmUpdate) {
          return;
        }
      }
      
      const updatedReservations = reservations.map(reservation => 
        reservation.id === updatedReservation.id 
          ? updatedReservation
          : reservation
      );
      await DataService.saveReservations(updatedReservations.map(convertReservationToExtended));
      setReservations(updatedReservations);
      setSelectedAppointment(updatedReservation);
      showNotification('予約を更新しました', 'success');
    } catch (error) {
      logger.error('予約更新エラー:', error);
      showNotification('予約の更新に失敗しました', 'error');
    }
  };


  // ローディング表示
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">予約データを読み込み中...</p>
        </div>
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <div className="p-6">
        <Alert className="mb-4 bg-red-50 text-red-900 border-red-200">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => window.location.reload()}>
          再読み込み
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー部分 */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            予約管理
          </h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {/* カレンダー/リスト切り替えタブ */}
          <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'calendar' | 'list')}>
            <TabsList>
              <TabsTrigger value="calendar">カレンダー</TabsTrigger>
              <TabsTrigger value="list">リスト</TabsTrigger>
            </TabsList>
          </Tabs>

          <Button
            onClick={() => setNewReservationModalOpen(true)}
            className="min-h-[44px]"
          >
            <Plus className="mr-2 h-4 w-4" />
            新規予約
          </Button>
        </div>
      </div>

      {/* AvailabilitySummaryBar （既存維持） */}
      <AvailabilitySummaryBar
        selectedDate={selectedDateString}
        appointments={appointmentsData}
        onPeriodClick={handlePeriodClick}
      />

      {/* 日付ナビゲーション */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => changeDate(-1)}
            className="min-w-[40px] p-2"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-lg font-semibold min-w-[200px] text-center">
            {format(currentDate, 'yyyy年M月d日(E)', { locale: ja })}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => changeDate(1)}
            className="min-w-[40px] p-2"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-sm text-muted-foreground">
            予約数: <span className="text-foreground font-bold">{stats.appointmentCount}件</span>
          </p>
          <p className="text-sm text-muted-foreground">
            売上予定: <span className="text-primary font-bold">¥{stats.totalRevenue.toLocaleString()}</span>
          </p>
        </div>
      </div>

      {/* メイン2カラムレイアウト */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        {/* 左カラム: カレンダー/リスト */}
        <Card>
          <CardHeader>
            <CardTitle>
              {viewMode === "calendar" ? "タイムスケジュール" : "予約一覧"}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {format(currentDate, 'M月d日(E)', { locale: ja })}の予約状況
            </p>
          </CardHeader>
          <CardContent>
            {viewMode === "calendar" ? (
              <StaffColumnCalendarView
                reservations={reservations as any}
                selectedDate={currentDate}
                staffList={mockStaff}
                onAppointmentClick={setSelectedAppointment as any}
                onEmptySlotClick={(time) => {
                  // 営業時間チェック
                  const selectedDateString = format(currentDate, 'yyyy-MM-dd');
                  const isOpenAtTime = isOpenAt(selectedDateString, time);
                  
                  if (!isOpenAtTime) {
                    showNotification(`${time}は営業時間外です`, 'warning');
                    return;
                  }
                  
                  const newDate = new Date(currentDate);
                  const [hours, minutes] = time.split(':');
                  newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                  setNewReservationModalOpen(true);
                }}
              />
            ) : (
              <AppointmentListView
                appointments={todayAppointments as any}
                onAppointmentClick={setSelectedAppointment as any}
              />
            )}
          </CardContent>
        </Card>

        {/* 右カラム: 予約詳細固定表示 */}
        <Card>
          <CardHeader>
            <CardTitle>
              予約詳細
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedAppointment ? "" : "予約を選択してください"}
            </p>
          </CardHeader>
          <CardContent>
            {selectedAppointment ? (
              <ExtendedAppointmentDetails
                appointment={selectedAppointment as any}
                onEdit={() => {
                  // 編集機能は ExtendedAppointmentDetails 内の ReservationEditModal で処理される
                  logger.log('編集機能を使用: ', selectedAppointment.id);
                }}
                onUpdate={handleReservationUpdate as any}
                onDelete={async () => {
                  const isConfirmed = window.confirm(
                    `【予約キャンセル確認】\n\n${selectedAppointment.customerName}様の予約をキャンセルしますか？\n\n日時: ${safeDateFormat(selectedAppointment.date, 'M月d日')} ${selectedAppointment.startTime}〜\nメニュー: ${selectedAppointment.serviceName}\n\nこの操作は取り消せません。`
                  );
                  if (isConfirmed) {
                    try {
                      const updatedReservations = reservations.filter(res => res.id !== selectedAppointment.id);
                      await DataService.saveReservations(updatedReservations.map(convertReservationToExtended));
                      setReservations(updatedReservations);
                      setSelectedAppointment(null);
                      showNotification('予約をキャンセルしました', 'success');
                    } catch (error) {
                      logger.error('予約キャンセルエラー:', error);
                      showNotification('予約のキャンセルに失敗しました', 'error');
                    }
                  }
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-[300px] text-center">
                <div>
                  <Calendar className="h-12 w-12 mb-2 mx-auto opacity-50 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    左側のスケジュールから予約を選択してください
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 新規予約モーダル（営業時間データ付き） */}
      <NewReservationModal
        open={newReservationModalOpen}
        onClose={() => setNewReservationModalOpen(false)}
        onSave={handleSaveReservation}
        staff={mockStaff}
        services={services}
        customers={customers}
        businessHoursValidation={{
          validateReservationTime,
          isOpenAt,
          getBusinessHoursForDate,
          businessHoursLoading,
          businessHoursError
        }}
        existingReservations={reservations.map(convertReservationToExtended)}
      />
    </div>
  );
};

export default Reservations;